import { useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  capabilitiesForRoles,
  primaryRole,
  PRODUCT_ROLE_LABEL,
  type Capability,
} from "@/services/auth/permissions";

export type PermissionsApi = {
  readonly roles: readonly string[];
  readonly primaryRole: string;
  readonly productLabel: string;
  readonly capabilities: readonly Capability[];
  can: (capability: Capability) => boolean;
  canAny: (...capabilities: Capability[]) => boolean;
};

/**
 * Single UI entry point for capability checks.
 * Never the sole security boundary — GraphQL still enforces authorization.
 */
export function usePermissions(): PermissionsApi {
  const auth = useAuth();

  return useMemo(() => {
    const roles = auth.user?.roles ?? [];
    const capabilities = capabilitiesForRoles(roles);
    const primary = primaryRole(roles);
    const set = new Set(capabilities);
    return {
      roles,
      primaryRole: primary,
      productLabel: PRODUCT_ROLE_LABEL[primary] ?? primary.toLowerCase(),
      capabilities,
      can: (capability: Capability) => set.has(capability),
      canAny: (...caps: Capability[]) => caps.some((c) => set.has(c)),
    };
  }, [auth.user?.roles]);
}
